
import React, { useState, createContext, useContext, ReactNode } from 'react';
import TactilePrompt from '../components/TactilePrompt';

interface PromptContextType {
  prompt: (title: string, message: string, placeholder?: string) => Promise<string | null>;
}

const PromptContext = createContext<PromptContextType | undefined>(undefined);

export const PromptProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [placeholder, setPlaceholder] = useState('');
  const [resolver, setResolver] = useState<(value: string | null) => void>(() => {});

  const prompt = (title: string, message: string, placeholder?: string): Promise<string | null> => {
    setTitle(title);
    setMessage(message);
    setPlaceholder(placeholder || '');
    setIsOpen(true);
    return new Promise((resolve) => {
      setResolver(() => resolve);
    });
  };

  const handleConfirm = (value: string) => {
    setIsOpen(false);
    resolver(value);
  };

  const handleCancel = () => {
    setIsOpen(false);
    resolver(null);
  };

  return (
    <PromptContext.Provider value={{ prompt }}>
      {children}
      <TactilePrompt
        isOpen={isOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title={title}
        message={message}
        placeholder={placeholder}
      />
    </PromptContext.Provider>
  );
};

export const usePrompt = () => {
  const context = useContext(PromptContext);
  if (!context) {
    throw new Error('usePrompt must be used within a PromptProvider');
  }
  return context;
};
